import { Cartesian3, Cesium3DTileset, Entity, Model } from "@cesium/engine";
import EditObject from "./wrapper/EditObject";
import PointEntity from "./PointEntity";
import LinestringEntity from "./LinestringEntity";
import PolygonEntity from "./PolygonEntity";

/**
 * 객체 크기조절을 위한 기능 클래스.<br>
 * A functionality class for object scale. <br>
 * ※ 주의사항(Note). </br>
 * 일반 Entity 객체에서는 기능이 제한됩니다. <br>
 * Functionality is limited on regular Entity objects..
 * @alias EditScale
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = await Ditap.Model.fromGltfAsync{
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // 1-1. 입력된 좌표 값으로 모델 이동(위경도 좌표)
 * const setScale = new Ditap.Cartesian3(5, 5, 5);
 * EditScale.setScale(model, setScale);
 * // 1-2. 입력된 좌표 값으로 모델 이동(데카르트 좌표)
 * const addScale = new Ditap.Cartesian3(5, 5, 5);
 * EditScale.addScale(model, addScale);
 *
 * // 3. 마우스로 객체 크기 조절
 * const interactionScale = new Ditap.InteractionScale(viewer);
 * interactionScale.on();
 */
class EditScale {
  constructor() {
    //super();
  }

  /**
   * 모델의 scale 적용.<br>
   * Set scale
   * @param {Model | Cesium3DTileset} object scale 적용할 모델.<br> The model to which to apply the scale
   * @param {Cartesian3 | Property} scale scale값. <br> Scale value
   * @returns {Cartesian3 | Property} 적용된 스케일.<br> Applied scale.
   * @example
   * const viewer = new Ditap.DitapViewer("ditapContainer");
   *
   * const destination = Ditap.Cartesian3.fromDegrees(
   *   126.978275264,
   *   37.566642192,
   *   400
   * );
   *
   * // 카메라 이동
   * viewer.scene.camera.flyTo({
   *   destination: destination,
   * });
   *
   * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
   *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
   * );
   * const model = await Ditap.Model.fromGltfAsync{
   *  id: "model_01",
   *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
   *  modelMatrix: modelMatrix,
   *  scale: 3,
   * });
   * viewer.scene.primitives.add(model);
   *
   * EditScale.setScale(model, new Ditap.Cartesian3(5, 5, 5));
   */
  static setScale(object, scale) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditScale.setScale object parameter is invalid");
    }

    try {
      // 오브젝트 업데이트
      const result = EditObject.setScale(object, scale);

      return result;
    } catch (error) {
      throw Error(`EditScale.setScale ${error}`);
    }
  }

  /**
   * 모델의 scale 추가.<br>
   * Add scale.
   * @param {Model | Cesium3DTileset} object scale 추가할 모델.<br> The model to which to add the scale
   * @param {Cartesian3} scale scale값. <br> Scale value
   * @returns {Cartesian3} 적용된 스케일.<br> Applied scale.
   * @example
   * const viewer = new Ditap.DitapViewer("ditapContainer");
   *
   * const destination = Ditap.Cartesian3.fromDegrees(
   *   126.978275264,
   *   37.566642192,
   *   400
   * );
   *
   * // 카메라 이동
   * viewer.scene.camera.flyTo({
   *   destination: destination,
   * });
   *
   * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
   *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
   * );
   * const model = await Ditap.Model.fromGltfAsync{
   *  id: "model_01",
   *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
   *  modelMatrix: modelMatrix,
   *  scale: 3,
   * });
   * viewer.scene.primitives.add(model);
   *
   * EditScale.addScale(model, new Ditap.Cartesian3(5, 5, 5));
   */
  static addScale(object, scale) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditScale.addScale object parameter is invalid");
    }

    try {
      // 오브젝트 Scale 추출
      const objectScale = EditObject.getScale(object);

      // 오브젝트 Scale + scale
      const sum = Cartesian3.add(objectScale, scale, new Cartesian3());

      // 오브젝트
      const result = EditObject.setScale(object, sum);

      return result;
    } catch (error) {
      throw `EditScale.addScale ${error}`;
    }
  }

  /**
   * 객체의 스케일.<br>
   * Scale of the object.
   * @param {Model | Cesium3DTileset | Entity} object 스케일을 구할 객체.<br> Object to determine scale.
   * @returns {Cartesian3} 스케일.<br> Scale.
   */
  static getScale(object) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditScale.getScale object parameter is invalid");
    }

    try {
      const scale = EditObject.getScale(object);

      return scale;
    } catch (error) {
      throw `EditScale.getScale ${error}`;
    }
  }

  /**
   * 객체의 트랜스폼.<br>
   * Transform of the object.
   * @param {Model | Cesium3DTileset | Entity} object 트랜스폼을 구할 객체.<br> Object to obtain transform.
   * @param {boolean} [enu=true] East-North-Up 기준 추출.<br> East-North-Up criteria extraction.
   * @returns {{
   *  position: Cartesian3,
   *  originalPosition: Cartesian3,
   *  translation: Cartesian3,
   *  quaternion: Quaternion,
   *  HeadingPitchRoll: HeadingPitchRoll,
   *  scale: Cartesian3
   * }} 트랜스폼 정보.<br> Transform info.
   */
  static decomposite(object, enu = true) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditScale.decomposite object parameter is invalid");
    }

    try {
      const decomposite = EditObject.decomposite(object, enu);

      return decomposite;
    } catch (error) {
      throw `EditScale.decomposite ${error}`;
    }
  }
}

export default EditScale;
